import React, { useState } from 'react';
import { Sparkles, Layers, Eye, Award, CheckCircle, XCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

// Elements by NexAir Logo (with light background - use blend mode)
const ELEMENTS_LOGO = 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/54bbp08l_IMG_1413.png';

// Elements Hero Video
const ELEMENTS_VIDEO = '/videos/nexair_show.mp4';

// Elements Gallery Videos
const ELEMENTS_GALLERY_VIDEOS = [
  { url: '/videos/nexair_show.mp4', title: 'Show Nocturno Elements', type: 'video' },
  { url: '/videos/nexair_show_2.mp4', title: 'Espectáculo de Luz y Agua', type: 'video' },
];

const SKY_MEETS_WATER_GALLERY = [
  ...ELEMENTS_GALLERY_VIDEOS,
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png', title: 'Boda con drones y water screen', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zio3060d_9FD265EA-9199-4903-B7B9-E7CA7AB0CF82.png', title: 'Show combinado', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/83vl4cac_A19EEFEF-D39C-4D93-9DD6-D0DD77F2074C.png', title: 'Paradise Resorts', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/qsz97onz_94F891CD-7FDF-4D0C-A27A-92E6AD27A26B.png', title: 'Tigre espectacular', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/gt8djmb8_3EFC8447-B984-4D7B-9C85-1271651DC812.png', title: 'Castle Hotels', type: 'image' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xs24fn5p_0F8C6FC4-D615-4552-9E9A-CFC630B64A14.png', title: 'Luminara Residences', type: 'image' },
];

const benefitIcons = [Layers, Eye, Sparkles, Award];
const benefitKeys = ['multidimensional', 'wowEffect', 'narrative', 'exclusivity'];

const SkyMeetsWaterPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { t, language } = useLanguage();
  const page = t('seoPages.skyMeetsWater');

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Elements by NexAir | Drones + Pantalla de Agua + Fuego | NexAir México" : "Elements by NexAir | Drones + Water Screen + Fire | NexAir Mexico"}
        description={language === 'es' ? "La experiencia más completa: shows de drones sincronizados con pantallas de agua y efectos de fuego. Cielo, agua y fuego en perfecta armonía para eventos únicos en México." : "The most complete experience: drone shows synchronized with water screens and fire effects. Sky, water and fire in perfect harmony for unique events in Mexico."}
        keywords={language === 'es' ? "elements by nexair, drones y agua, show combinado, espectaculo drones agua fuego, evento premium, experiencia inmersiva" : "elements by nexair, drones and water, combined show, drone water fire spectacle, premium event, immersive experience"}
        canonicalUrl="https://nexair.com.mx/elements"
      />
      
      {/* Video Hero with Elements Logo */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            poster="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zdmzsrqn_6807D169-3301-4818-92FC-61CA37977AB4.png"
            className="w-full h-full object-cover"
          >
            <source src={ELEMENTS_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#030810] via-[#030810]/50 to-[#030810]/30" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20">
          {/* Elements Logo */}
          <img 
            src={ELEMENTS_LOGO} 
            alt="Elements by NexAir" 
            className="h-64 md:h-80 lg:h-96 w-auto mx-auto mb-4 object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
          <p className="text-xl md:text-2xl text-white/90 font-light mb-4">
            {page.subtitle}
          </p>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {page.description}
          </p>
        </div>
      </section>
      
      {/* Intro Section */}
      <PageSection className="bg-[#030810]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {page.newDimension} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{page.newDimensionHighlight}</span>
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            {page.introDesc1}
          </p>
          <p className="text-gray-300 text-lg">
            {page.introDesc2}
          </p>
        </div>
      </PageSection>
      
      {/* Benefits */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.whyChoose}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitKeys.map((key, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{page.benefits[key].title}</h3>
                <p className="text-gray-400">{page.benefits[key].desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* Process */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.processTitle}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {page.processSteps.map((item, index) => (
            <div key={index} className="relative">
              <div className="text-6xl font-bold text-cyan-500/20 mb-4">{item.step}</div>
              <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
              <p className="text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
          {page.galleryTitle}
        </h2>
        <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
          {page.galleryDesc}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {SKY_MEETS_WATER_GALLERY.map((item, index) => (
            <div 
              key={index}
              className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer relative group"
              onClick={() => setLightboxImage(item)}
            >
              {item.type === 'video' ? (
                <video 
                  src={item.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <img 
                  src={item.url} 
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              )}
              {/* Video indicator */}
              {item.type === 'video' && (
                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  Video
                </div>
              )}
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Ideal For */}
      <PageSection className="bg-[#030810]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {page.idealFor}
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              {page.idealForDesc}
            </p>
            <div className="space-y-3">
              {page.idealForList.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png"
              alt="Sky Meets Water"
              className="rounded-2xl border border-white/10 w-full"
              loading="lazy"
            />
          </div>
        </div>
      </PageSection>
      
      <PageCTA 
        title={page.ctaTitle}
        subtitle={page.ctaSubtitle}
        whatsappMessage={page.ctaWhatsapp}
      />

      {/* Lightbox for images and videos */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightboxImage.type === 'video' ? (
              <video 
                src={lightboxImage.url}
                autoPlay
                controls
                playsInline
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            ) : (
              <img 
                src={lightboxImage.url} 
                alt={lightboxImage.title}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            )}
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default SkyMeetsWaterPage;
